/**
 * Текущие позиции инспекторов для экрана /admin/inspectors-positions.
 *
 * Источник: одна строка на инспектора в `profiles` (`last_lat`, `last_lon`,
 * `last_position_at`). История точек на фазе 1 не ведётся. Активный заказ —
 * через JOIN на `orders` по списку активных статусов (см. миграцию
 * `20260504000000_geo_phase1.sql`).
 *
 * RLS: SELECT на `profiles` для admin/dispatcher уже открыт (см.
 * `20260420000300_phase1_rls_update.sql`). UPDATE координат — admin only,
 * политика обычная (admin-update-any) уже есть.
 */
import { createClient } from '@/api/supabase'

export const ACTIVE_INSPECTOR_STATUSES = [
  'ASSIGNED',
  'DRIVING',
  'ARRIVED',
  'INSPECTION',
  'APPRAISAL',
  'BUYING',
  'DOCUMENT_PREPARATION',
  'PAYMENT_TRANSFER',
  'TRANSPORTATION',
] as const

export interface InspectorPositionRow {
  id: string
  full_name: string | null
  phone: string | null
  last_lat: number | null
  last_lon: number | null
  last_address: string | null
  last_position_at: string | null
  /** Тестовый режим: при true RPC `report_my_position` игнорирует входящие
   *  GPS-точки → виртуальные позиции не перезатираются. Управляется через
   *  `setInspectorPositionSimulated()`. */
  position_simulated: boolean
  /** Текущий режим трекера на устройстве инспектора. NULL — клиент ни разу
   *  не отправлял статус (старая версия / не инспектор). */
  tracking_mode: 'off' | 'idle' | 'active_order' | null
  /** Статус permission геолокации на устройстве. */
  permission_status:
    | 'granted'
    | 'while_using'
    | 'denied'
    | 'denied_forever'
    | 'restricted'
    | 'unknown'
    | null
  /** Последняя ошибка отправки точки. NULL = всё ок / нет данных. */
  last_geo_error: string | null
  /** Размер локального буфера несинхронизированных точек на устройстве. */
  pending_buffer_size: number
  /** Когда последний раз пришёл статус трекера. */
  tracking_status_updated_at: string | null
  /** Заявленная точность последней GPS-точки в метрах
   *  (CLLocation.horizontalAccuracy / Android Location.accuracy). NULL —
   *  клиент не передавал. Большие значения (>=1000м) — типично iOS Approximate
   *  Location, координаты при этом снэпятся к ~1км сетке и трек выглядит как
   *  2-3 точки за всю смену. */
  last_position_accuracy_m: number | null
  /** Когда от инспектора последний раз пришёл keepalive (отдельный «пульс»,
   *  независимый от GPS-точек). NULL — мобильный клиент не шлёт keepalive
   *  (старая версия), либо записи в `inspector_keepalive` ещё нет. */
  last_keepalive_at: string | null
  active_order: {
    id: string
    status: string
  } | null
}

export interface ListInspectorsParams {
  page?: number
  pageSize?: number
  search?: string
  freshOnly?: boolean
  freshMinutes?: number
}

export interface ListInspectorsResult {
  rows: InspectorPositionRow[]
  total: number
}

export async function listInspectorsPositions(
  params: ListInspectorsParams = {},
): Promise<ListInspectorsResult> {
  const {
    page = 1,
    pageSize = 50,
    search = '',
    freshOnly = false,
    freshMinutes = 5,
  } = params
  const supabase = createClient()

  // 1) profiles + count и 2) активные заказы — параллельно.
  //    Embedded JOIN на orders невозможен: FK orders.inspector_id указывает
  //    на auth.users, а не на public.profiles. Раньше запрос orders ждал
  //    inspectorIds со страницы — это давало лишний RTT. На фазе 1 у инспектора
  //    максимум один активный заказ (idx_one_active_order_per_inspector),
  //    активных мало → дешевле тянуть всех и матчить на клиенте.
  //    count=estimated: для пагинации хватит, exact на каждой странице
  //    прогонял RLS по всему profiles.
  let q = supabase
    .from('profiles')
    .select(
      'id, full_name, phone, last_lat, last_lon, last_address, last_position_at, last_position_accuracy_m, position_simulated, tracking_mode, permission_status, last_geo_error, pending_buffer_size, tracking_status_updated_at',
      { count: 'estimated' },
    )
    .eq('role', 'inspector')
    .eq('is_active', true)

  if (search.trim()) {
    q = q.ilike('full_name', `%${search.trim()}%`)
  }

  if (freshOnly) {
    const sinceIso = new Date(Date.now() - freshMinutes * 60 * 1000).toISOString()
    q = q.gte('last_position_at', sinceIso)
  }

  // Сортировка: свежие сверху. NULLS last.
  q = q.order('last_position_at', { ascending: false, nullsFirst: false })

  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  q = q.range(from, to)

  const ordersQuery = supabase
    .from('orders')
    .select('id, inspector_id, status')
    .in('status', ACTIVE_INSPECTOR_STATUSES as unknown as string[])

  // Параллельно тянем keepalive по всем инспекторам (таблица узкая, не в realtime —
  // см. миграцию `20260511150000_inspector_keepalive.sql`). Поллинг свежести
  // делается через `refetchInterval` на стороне queryClient.
  const keepaliveQuery = supabase
    .from('inspector_keepalive')
    .select('inspector_id, last_keepalive_at')

  const [profilesRes, ordersRes, keepaliveRes] = await Promise.all([
    q,
    ordersQuery,
    keepaliveQuery,
  ])

  if (profilesRes.error) throw new Error(profilesRes.error.message)
  if (ordersRes.error) throw new Error(ordersRes.error.message)
  // keepalive: не критично если упало (старая БД до миграции).
  if (keepaliveRes.error) {
    console.warn('[inspectors-positions] keepalive read failed:', keepaliveRes.error.message)
  }

  const profileRows = (profilesRes.data ?? []) as Array<{
    id: string
    full_name: string | null
    phone: string | null
    last_lat: number | null
    last_lon: number | null
    last_address: string | null
    last_position_at: string | null
    last_position_accuracy_m: number | null
    position_simulated: boolean | null
    tracking_mode: 'off' | 'idle' | 'active_order' | null
    permission_status: InspectorPositionRow['permission_status']
    last_geo_error: string | null
    pending_buffer_size: number | null
    tracking_status_updated_at: string | null
  }>
  const count = profilesRes.count

  const ordersList = (ordersRes.data ?? []) as Array<{
    id: string
    inspector_id: string | null
    status: string
  }>
  const activeByInspector = new Map<string, { id: string; status: string }>()
  for (const o of ordersList) {
    if (o.inspector_id) {
      activeByInspector.set(o.inspector_id, { id: o.id, status: o.status })
    }
  }

  const keepaliveList = (keepaliveRes.data ?? []) as Array<{
    inspector_id: string
    last_keepalive_at: string
  }>
  const keepaliveByInspector = new Map<string, string>()
  for (const k of keepaliveList) {
    keepaliveByInspector.set(k.inspector_id, k.last_keepalive_at)
  }

  const rows: InspectorPositionRow[] = profileRows.map((p) => ({
    id: p.id,
    full_name: p.full_name,
    phone: p.phone,
    last_lat: p.last_lat,
    last_lon: p.last_lon,
    last_address: p.last_address,
    last_position_at: p.last_position_at,
    last_position_accuracy_m: p.last_position_accuracy_m,
    position_simulated: Boolean(p.position_simulated),
    tracking_mode: p.tracking_mode,
    permission_status: p.permission_status,
    last_geo_error: p.last_geo_error,
    pending_buffer_size: p.pending_buffer_size ?? 0,
    tracking_status_updated_at: p.tracking_status_updated_at,
    last_keepalive_at: keepaliveByInspector.get(p.id) ?? null,
    active_order: activeByInspector.get(p.id) ?? null,
  }))

  return { rows, total: count ?? rows.length }
}

/**
 * Обновляет позицию инспектора. Только admin (RLS на profiles).
 *
 * `address` — текстовое представление точки (то, что админ ввёл/выбрал
 * в Geosuggest). Хранится для отображения в таблице. Может быть null
 * (например, если позиция выставляется не из модалки).
 */
export async function updateInspectorPosition(
  inspectorId: string,
  lat: number,
  lon: number,
  address: string | null = null,
): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('profiles')
    .update({
      last_lat: lat,
      last_lon: lon,
      last_address: address,
      last_position_at: new Date().toISOString(),
    })
    .eq('id', inspectorId)
    .eq('role', 'inspector')
  if (error) throw new Error(error.message)
}

/**
 * Переключает флаг `profiles.position_simulated`. При `true` устройство-RPC
 * `report_my_position` начинает игнорировать входящие GPS-точки, и
 * виртуальные позиции, выставленные админом, перестают перезатираться.
 *
 * Только admin/dispatcher (RLS — RPC `set_inspector_position_simulated`
 * сама проверяет роль через `current_user_role()`).
 */
export async function setInspectorPositionSimulated(
  inspectorId: string,
  simulated: boolean,
): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.rpc('set_inspector_position_simulated', {
    p_inspector_id: inspectorId,
    p_simulated: simulated,
  })
  if (error) throw new Error(error.message)
}

export interface RandomizeResult {
  ok: true
  total: number
  updated: number
  failed: number
}

/**
 * Дёргает edge-функцию `randomize-inspector-positions`: всем активным
 * инспекторам ставит случайные координаты в районе Москвы и реальный адрес
 * через Yandex reverse-geocoder. Только admin (проверка и в edge-функции).
 */
export async function randomizeAllInspectorPositions(): Promise<RandomizeResult> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke<RandomizeResult>(
    'randomize-inspector-positions',
    { body: {} },
  )
  if (error) throw new Error(error.message)
  if (!data) throw new Error('empty_response')
  return data
}

export interface RefreshAddressesResult {
  ok: true
  total: number
  updated: number
  skipped: number
  failed: number
}

/**
 * Дёргает edge-функцию `refresh-inspector-addresses`: для всех РЕАЛЬНЫХ
 * инспекторов (`position_simulated=false`) с непустыми `last_lat/last_lon`
 * подтягивает актуальный адрес через Yandex reverse-geocoder и записывает
 * в `last_address` + `last_address_lat/lon`. Stale-фильтр на стороне функции:
 * если позиция сместилась ≤ 200м от точки адреса — пропускаем. TEST-инспекторы
 * не затрагиваются (для них есть `randomize-inspector-positions`).
 * Только admin (проверка и в edge-функции).
 */
export async function refreshRealInspectorAddresses(): Promise<RefreshAddressesResult> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke<RefreshAddressesResult>(
    'refresh-inspector-addresses',
    { body: {} },
  )
  if (error) throw new Error(error.message)
  if (!data) throw new Error('empty_response')
  return data
}

/**
 * Дёргает edge-функцию `randomize-inspector-times`: всем активным инспекторам
 * с уже установленной позицией ставит случайное `last_position_at` от
 * 5 секунд до 65 минут назад. Используется только для отладки индикатора
 * свежести; admin-only (проверка и в edge-функции).
 */
export async function randomizeAllInspectorTimes(): Promise<RandomizeResult> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke<RandomizeResult>(
    'randomize-inspector-times',
    { body: {} },
  )
  if (error) throw new Error(error.message)
  if (!data) throw new Error('empty_response')
  return data
}
